import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  applyActionCode,
  checkActionCode,
  signInWithEmailAndPassword,
  updatePassword,
  type AuthError,
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { api } from '../lib/api';
import { getSignupData, clearSignupData } from './AuthPage';
import { isPasswordValid } from '../lib/password';
import { PasswordChecklist } from '../components/ui/PasswordChecklist';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

// ── Shared input class (same as AuthPage) ──────────────────────────────────
const inputClass =
  'w-full rounded-lg border border-stone bg-white px-4 py-3 text-sm ' +
  'text-midnight placeholder:text-slate outline-none ' +
  'focus:border-plum focus:ring-2 focus:ring-plum/20 transition-all duration-150';

const inputErrorClass =
  'w-full rounded-lg border border-red-400 bg-white px-4 py-3 text-sm ' +
  'text-midnight placeholder:text-slate outline-none ' +
  'focus:border-red-400 focus:ring-2 focus:ring-red-200 transition-all duration-150';

type PageState =
  | { kind: 'verifying' }
  | { kind: 'password-form'; email: string; name: string; tempPassword: string }
  | { kind: 'no-signup-data'; email: string }
  | { kind: 'error'; message: string }
  | { kind: 'submitting' }
  | { kind: 'done' };

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');

  const [state, setState] = useState<PageState>({ kind: 'verifying' });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const customToken = searchParams.get('token');

  // ── Verify email on mount ───────────────────────────────────────────────
  useEffect(() => {
    async function verify() {
      const signupData = getSignupData();

      // Path 1: Custom verification token (our own system, bypasses Firebase rate limits)
      if (customToken) {
        try {
          const res = await api.post<{ success: boolean; data?: { email: string }; error?: string }>(
            '/auth/verify-email-token',
            { token: customToken },
          );
          const email = res.data?.email ?? signupData?.email ?? '';

          if (signupData && signupData.email.toLowerCase() === email.toLowerCase()) {
            setState({
              kind: 'password-form',
              email,
              name: signupData.name,
              tempPassword: signupData.tempPassword,
            });
          } else {
            setState({ kind: 'no-signup-data', email });
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Verification failed.';
          setState({ kind: 'error', message });
        }
        return;
      }

      // Path 2: Firebase oobCode (legacy — for links already sent via Firebase)
      if (oobCode && mode === 'verifyEmail') {
        try {
          const info = await checkActionCode(auth, oobCode);
          const email = info.data.email ?? signupData?.email ?? '';
          await applyActionCode(auth, oobCode);

          if (signupData && signupData.email === email) {
            setState({
              kind: 'password-form',
              email,
              name: signupData.name,
              tempPassword: signupData.tempPassword,
            });
          } else {
            setState({ kind: 'no-signup-data', email });
          }
        } catch (err) {
          const code = (err as AuthError).code;
          if (code === 'auth/expired-action-code') {
            setState({ kind: 'error', message: 'This verification link has expired. Please sign up again.' });
          } else if (code === 'auth/invalid-action-code') {
            setState({ kind: 'error', message: 'This verification link is invalid or has already been used.' });
          } else {
            setState({ kind: 'error', message: 'Something went wrong verifying your email. Please try again.' });
          }
        }
        return;
      }

      // Path 3: No token or code — auto-verified user arriving at the continue URL
      if (signupData) {
        setState({
          kind: 'password-form',
          email: signupData.email,
          name: signupData.name,
          tempPassword: signupData.tempPassword,
        });
      } else {
        setState({
          kind: 'error',
          message: 'No account setup in progress. Please start from the sign-up page.',
        });
      }
    }

    verify();
  }, [oobCode, mode, customToken]);

  // ── Set password and finish account creation ────────────────────────────
  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (state.kind !== 'password-form') return;

    setSubmitError(null);

    if (password !== confirmPassword) {
      setSubmitError('Passwords do not match.');
      return;
    }
    if (!isPasswordValid(password)) {
      setSubmitError('Password does not meet the requirements.');
      return;
    }

    const { email, tempPassword } = state;
    setState({ kind: 'submitting' });

    try {
      // Sign in with the temporary password
      const cred = await signInWithEmailAndPassword(auth, email, tempPassword);

      // Update to the user's chosen password
      await updatePassword(cred.user, password);

      // Clean up
      clearSignupData();
      sessionStorage.removeItem('iomi_signup_email');

      setState({ kind: 'done' });

      // Navigate to the app — the auth state change will render the
      // authenticated routes automatically.
      navigate('/', { replace: true });
    } catch (err) {
      const code = (err as AuthError).code;
      if (code === 'auth/invalid-credential' || code === 'auth/wrong-password') {
        setState({
          kind: 'error',
          message: 'Unable to complete account setup. Please sign up again.',
        });
      } else {
        setSubmitError('Something went wrong. Please try again.');
        setState({
          kind: 'password-form',
          email: state.email,
          name: state.name,
          tempPassword: state.tempPassword,
        });
      }
    }
  }

  const passwordsMatch = password === confirmPassword;
  const canSubmit =
    isPasswordValid(password) && passwordsMatch && password.length > 0;

  return (
    <div className="min-h-screen bg-mist flex flex-col items-center justify-start pt-12 px-5 pb-12">
      <div className="w-full max-w-sm flex flex-col gap-6">

        {/* Hero */}
        <div className="text-center px-2 pt-4 pb-2">
          <h1 className="font-display text-display text-midnight leading-tight">
            Let's Get{' '}
            <span className="font-extrabold text-plum">Moving</span>
          </h1>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-brand-md border border-stone/60 px-5 py-6">

          {/* Verifying... */}
          {state.kind === 'verifying' && (
            <div className="flex flex-col items-center gap-3 py-6">
              <LoadingSpinner size="md" />
              <p className="text-sm text-slate">Verifying your email...</p>
            </div>
          )}

          {/* Submitting... */}
          {state.kind === 'submitting' && (
            <div className="flex flex-col items-center gap-3 py-6">
              <LoadingSpinner size="md" />
              <p className="text-sm text-slate">Setting up your account...</p>
            </div>
          )}

          {/* Error */}
          {state.kind === 'error' && (
            <div className="flex flex-col gap-5">
              <div>
                <h2 className="font-display text-h2 font-semibold text-midnight">
                  Verification Failed
                </h2>
                <p className="text-sm text-slate mt-2 leading-relaxed">
                  {state.message}
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/', { replace: true })}
                className="btn-primary w-full"
              >
                Back to Log In
              </button>
            </div>
          )}

          {/* No signup data — email verified but can't set password */}
          {state.kind === 'no-signup-data' && (
            <div className="flex flex-col gap-5">
              <div>
                <h2 className="font-display text-h2 font-semibold text-midnight">
                  Email Verified
                </h2>
                <p className="text-sm text-slate mt-2 leading-relaxed">
                  Your email <span className="font-semibold text-midnight">{state.email}</span> has been verified.
                  Please return to the device where you started signing up to finish setting your password.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/', { replace: true })}
                className="btn-primary w-full"
              >
                Go to Log In
              </button>
            </div>
          )}

          {/* Password form */}
          {state.kind === 'password-form' && (
            <div className="flex flex-col gap-5">
              <div>
                <h2 className="font-display text-h2 font-semibold text-midnight">
                  Set Your Password
                </h2>
                <p className="text-sm text-slate mt-1 leading-relaxed">
                  One last step — choose a password for your account.
                </p>
              </div>

              {/* Display name + email (read-only) */}
              <div className="flex flex-col gap-3 rounded-xl bg-mist px-4 py-3">
                <div>
                  <p className="text-[11px] text-slate uppercase tracking-wider font-medium">Name</p>
                  <p className="text-sm font-semibold text-midnight">{state.name}</p>
                </div>
                <div>
                  <p className="text-[11px] text-slate uppercase tracking-wider font-medium">Email</p>
                  <p className="text-sm font-semibold text-midnight">{state.email}</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="new-password" className="field-label">Password</label>
                  <input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Choose a password"
                    className={inputClass}
                  />
                  <PasswordChecklist password={password} />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="confirm-new-password" className="field-label">
                    Confirm Password
                  </label>
                  <input
                    id="confirm-new-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    className={confirmPassword && !passwordsMatch ? inputErrorClass : inputClass}
                  />
                  {confirmPassword && !passwordsMatch && (
                    <p className="text-[13px] text-red-500">Passwords do not match.</p>
                  )}
                </div>

                {submitError && (
                  <p role="alert" className="text-sm text-red-600">{submitError}</p>
                )}

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="btn-primary w-full"
                >
                  Finish Setup
                </button>
              </form>
            </div>
          )}

          {/* Done (brief flash before navigate) */}
          {state.kind === 'done' && (
            <div className="flex flex-col items-center gap-3 py-6">
              <LoadingSpinner size="md" />
              <p className="text-sm text-slate">Welcome! Signing you in...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
